import { Controller } from "@hotwired/stimulus";

import { prefersReducedMotion } from "~/lib/viewport";
import { Clock, fitStage, whileInView } from "~/lib/web/loop";

const STAGE_WIDTH = 704;

const REST_X = 40;
const REST_Y = 93;
const PITCH = 345;
const EXIT_X = 420;
const HERO_X = 40;
const HERO_Y = 30;
const HERO_SCALE = 1.32;

/** How far above its final position a confirmation chip starts. */
const CHIP_PARK = 62;

const EXIT_EASE = "cubic-bezier(0.4, 0, 0.2, 1)";

/**
 * Elastic. The overshoot peaks at 80% of the grow and is settled by the end of
 * it, so no later beat has to be delayed for it.
 */
const GROW_EASE = "cubic-bezier(0.34, 1.28, 0.5, 1)";

export default class WebPayController extends Controller<HTMLElement> {
  static override targets = ["stage", "card", "chip", "icon"];
  declare readonly stageTarget: HTMLElement;
  declare readonly hasStageTarget: boolean;
  declare readonly cardTargets: HTMLElement[];
  declare readonly chipTargets: HTMLElement[];
  declare readonly iconTargets: HTMLElement[];

  private clock = new Clock();
  private stop: (() => void) | null = null;
  private unfit: (() => void) | null = null;

  override connect(): void {
    if (!this.hasStageTarget || this.cardTargets.length < 2) return;
    this.unfit = fitStage(this.element, this.stageTarget, STAGE_WIDTH);

    if (prefersReducedMotion()) {
      this.showResolved();
      return;
    }
    this.stop = whileInView(
      this.element,
      () => {
        this.rest();
        this.run();
      },
      () => this.clock.clear(),
    );
  }

  override disconnect(): void {
    this.stop?.();
    this.stop = null;
    this.unfit?.();
    this.unfit = null;
    this.clock.clear();
  }

  private place(card: HTMLElement, x: number, y: number, scale: number): void {
    card.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
  }

  private rest(): void {
    this.cardTargets.forEach((card, index) => {
      card.style.transition = "none";
      this.place(card, REST_X + index * PITCH, REST_Y, 1);
    });
    for (const chip of this.chipTargets) {
      chip.style.transition = "none";
      chip.style.opacity = "0";
      chip.style.transform = `translateY(-${CHIP_PARK}px)`;
    }
    for (const icon of this.iconTargets) {
      icon.style.transition = "none";
      icon.style.transform = "scale(0)";
    }
    // Forced reflow, so the transitions written next start from these values.
    void this.stageTarget.offsetWidth;
  }

  /** The end state: the front card at full size and the neighbour off stage. */
  private showResolved(): void {
    this.rest();
    const [hero, ...rest] = this.cardTargets;
    if (hero === undefined) return;
    for (const card of rest) card.style.opacity = "0";
    this.place(hero, HERO_X, HERO_Y, HERO_SCALE);
    for (const chip of this.chipTargets) {
      chip.style.opacity = "1";
      chip.style.transform = "translateY(0)";
    }
    for (const icon of this.iconTargets) icon.style.transform = "scale(1)";
  }

  private chipIn(index: number, delay: number, duration: number): void {
    const chip = this.chipTargets[index];
    const icon = this.iconTargets[index];
    if (chip === undefined || icon === undefined) return;
    this.clock.at(delay, () => {
      chip.style.transition = `opacity 1ms linear, transform ${duration}ms ${GROW_EASE}`;
      chip.style.opacity = "1";
      chip.style.transform = "translateY(0)";
    });
    this.clock.at(delay + 260, () => {
      icon.style.transition = `transform 460ms ${GROW_EASE}`;
      icon.style.transform = "scale(1)";
    });
  }

  private run(): void {
    const [hero, ...rest] = this.cardTargets;
    if (hero === undefined) return;

    // The neighbour exits to the right, the side it is already cropped on.
    this.clock.at(360, () => {
      rest.forEach((card, offset) => {
        card.style.transition = `transform 700ms ${EXIT_EASE}`;
        this.place(card, REST_X + (offset + 1) * PITCH + EXIT_X, REST_Y, 1);
      });
    });
    this.clock.at(400, () => {
      hero.style.transition = `transform 900ms ${GROW_EASE}`;
      this.place(hero, HERO_X, HERO_Y, HERO_SCALE);
    });
    this.chipIn(0, 1620, 620);
    this.chipIn(1, 2180, 600);

    /* The loop reverses rather than cutting or fading: chips leave, the front
       card shrinks, the neighbour returns. The panel behind the cards is too
       pale for a stage fade, which reads as a flash of white. */
    this.clock.at(5200, () => {
      this.chipTargets.forEach((chip, index) => {
        chip.style.transition = `opacity 240ms linear 260ms, transform 500ms ${EXIT_EASE}`;
        chip.style.opacity = "0";
        chip.style.transform = `translateY(-${CHIP_PARK}px)`;
        const icon = this.iconTargets[index];
        if (icon === undefined) return;
        icon.style.transition = `transform 260ms ${EXIT_EASE}`;
        icon.style.transform = "scale(0)";
      });
    });
    this.clock.at(5700, () => {
      hero.style.transition = `transform 700ms ${EXIT_EASE}`;
      this.place(hero, REST_X, REST_Y, 1);
    });
    this.clock.at(5900, () => {
      rest.forEach((card, offset) => {
        card.style.transition = `transform 700ms ${EXIT_EASE}`;
        this.place(card, REST_X + (offset + 1) * PITCH, REST_Y, 1);
      });
    });
    // By now every value is what rest() would write, so the swap is not
    // visible.
    this.clock.at(6800, () => this.run());
  }
}
